import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  MapPin, Plus, Edit2, Phone, Truck, ShoppingCart,
  Save, ChevronRight, DollarSign, ArrowLeft,
  MessageCircle, RefreshCw, LogOut, Settings,
  CreditCard, Package, Users, BarChart3, Factory, Plug, Shield, Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { API, getAuthHeaders } from '../App';

export default function AdminCities({ user, onLogout }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || 'ru';
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    phone: '',
    online_orders_enabled: true,
    delivery_today_enabled: true,
    default_markup_percent: 0,
    sort_order: 0,
    offline_message: '',
    offline_contact_url: '',
    offline_contact_label: ''
  });

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API}/cities`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      setCities(data);
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCity 
        ? `${API}/cities/${editingCity.id}`
        : `${API}/cities`;
      
      const res = await fetch(url, {
        method: editingCity ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        fetchCities();
        setShowForm(false);
        setEditingCity(null);
        resetForm();
      }
    } catch (error) {
      console.error('Error saving city:', error);
    }
  };

  const handleEdit = (city) => {
    setEditingCity(city);
    setFormData({
      name: city.name,
      code: city.code,
      phone: city.phone || '',
      online_orders_enabled: city.online_orders_enabled,
      delivery_today_enabled: city.delivery_today_enabled,
      default_markup_percent: city.default_markup_percent || 0,
      sort_order: city.sort_order || 0,
      offline_message: city.offline_message || '',
      offline_contact_url: city.offline_contact_url || '',
      offline_contact_label: city.offline_contact_label || ''
    });
    setShowForm(true);
  };

  const handleToggleOnlineOrders = async (city) => {
    try {
      await fetch(`${API}/cities/${city.id}/online-orders?enabled=${!city.online_orders_enabled}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchCities();
    } catch (error) {
      console.error('Error toggling online orders:', error);
    }
  };

  const handleToggleDeliveryToday = async (city) => {
    try {
      await fetch(`${API}/cities/${city.id}/delivery-today?enabled=${!city.delivery_today_enabled}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchCities();
    } catch (error) {
      console.error('Error toggling delivery:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      phone: '',
      online_orders_enabled: true,
      delivery_today_enabled: true,
      default_markup_percent: 0,
      sort_order: 0,
      offline_message: '',
      offline_contact_url: '',
      offline_contact_label: ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Same as Dashboard */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}`} className="flex items-center">
                <img 
                  src="/logo.jpg" 
                  alt="Ainanan" 
                  className="h-10 object-contain"
                />
              </Link>
              <div className="hidden md:block">
                <h1 className="text-lg font-semibold text-gray-900">Города</h1>
                <p className="text-xs text-gray-500">Управление городами и ценами</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchCities(true)}
                disabled={refreshing}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Выход
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Header with Back to Management */}
      <div className="bg-white border-b">
        <div className="max-w-[1600px] mx-auto px-4">
          <div className="flex items-center gap-4 py-3">
            <Button onClick={() => navigate(`/${currentLang}/admin/settings`)} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Управление
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Управление городами</h2>
            <p className="text-gray-500">Настройте города, цены и доступность заказов</p>
          </div>
          <Button 
            onClick={() => { setShowForm(true); setEditingCity(null); resetForm(); }}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Добавить город
          </Button>
        </div>

        {/* Cities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cities.map(city => (
            <Card 
              key={city.id} 
              className={`overflow-hidden transition-all hover:shadow-lg ${
                !city.is_active ? 'opacity-60' : ''
              }`}
            >
              {/* City Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-6 h-6 text-white" />
                    <h3 className="text-xl font-bold text-white">{city.name}</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(city)}
                    className="text-white hover:bg-white/20"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                {city.phone && (
                  <div className="flex items-center gap-2 mt-2 text-white/90">
                    <Phone className="w-4 h-4" />
                    <span className="text-sm">{city.phone}</span>
                  </div>
                )}
              </div>

              <CardContent className="p-5 space-y-4">
                {/* Online Orders Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <ShoppingCart className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium">Онлайн-заказы</span>
                  </div>
                  <button
                    onClick={() => handleToggleOnlineOrders(city)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      city.online_orders_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        city.online_orders_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Delivery Today Toggle */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Truck className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium">Доставка сегодня</span>
                  </div>
                  <button
                    onClick={() => handleToggleDeliveryToday(city)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      city.delivery_today_enabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        city.delivery_today_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Default Markup */}
                {city.default_markup_percent > 0 && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm text-emerald-700">
                      Наценка по умолчанию: <strong>+{city.default_markup_percent}%</strong>
                    </span>
                  </div>
                )}

                {/* Price List Link */}
                <Button
                  variant="outline"
                  className="w-full justify-between"
                  onClick={() => navigate(`/${currentLang}/admin/cities/${city.id}/prices`)}
                >
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    <span>Прайс-лист</span>
                  </div>
                  <ChevronRight className="w-4 h-4" />
                </Button>

                {/* Status Badge - Custom offline message */}
                {!city.online_orders_enabled && (
                  <div className="space-y-2 pt-2 border-t">
                    <div className="bg-orange-100 text-orange-700 px-3 py-2 rounded-lg text-sm text-center font-medium">
                      {city.offline_message || 'Только заказы по телефону'}
                    </div>
                    {city.offline_contact_url && (
                      <a 
                        href={city.offline_contact_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 bg-green-500 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-600 transition-colors"
                      >
                        <MessageCircle className="w-4 h-4" />
                        {city.offline_contact_label || 'Связаться'}
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {cities.length === 0 && (
          <Card className="p-12">
            <div className="text-center">
              <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">Нет городов</h3>
              <p className="text-gray-500 mb-6">Добавьте первый город для начала работы</p>
              <Button
                onClick={() => setShowForm(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Добавить город
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* City Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <CardTitle>
                {editingCity ? 'Редактировать город' : 'Новый город'}
              </CardTitle>
            </CardHeader>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Название города
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Например: Астана"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Код города (латиница)
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toLowerCase()})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Например: astana"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Телефон
                </label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="+7 XXX XXX XX XX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Наценка по умолчанию (%)
                </label>
                <input
                  type="number"
                  value={formData.default_markup_percent}
                  onChange={(e) => setFormData({...formData, default_markup_percent: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                  max="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Будет применяться к новым товарам
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Порядок сортировки
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.online_orders_enabled}
                    onChange={(e) => setFormData({...formData, online_orders_enabled: e.target.checked})}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <span className="text-sm">Онлайн-заказы</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.delivery_today_enabled}
                    onChange={(e) => setFormData({...formData, delivery_today_enabled: e.target.checked})}
                    className="w-4 h-4 text-emerald-600 rounded"
                  />
                  <span className="text-sm">Доставка сегодня</span>
                </label>
              </div>

              {/* Offline message settings - shown when online orders are disabled */}
              {!formData.online_orders_enabled && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  <div className="flex items-center gap-2 text-orange-600">
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-medium">Настройки офлайн-режима</span>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Сообщение для клиентов
                    </label>
                    <input
                      type="text"
                      value={formData.offline_message}
                      onChange={(e) => setFormData({...formData, offline_message: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Заказы принимаем по WhatsApp"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ссылка для связи (WhatsApp, Telegram и т.д.)
                    </label>
                    <input
                      type="text"
                      value={formData.offline_contact_url}
                      onChange={(e) => setFormData({...formData, offline_contact_url: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="https://wa.me/77771234567"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      WhatsApp: https://wa.me/77771234567
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Текст кнопки
                    </label>
                    <input
                      type="text"
                      value={formData.offline_contact_label}
                      onChange={(e) => setFormData({...formData, offline_contact_label: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Написать в WhatsApp"
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowForm(false); setEditingCity(null); resetForm(); }}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Сохранить
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
